/**
 * Script Browser Store
 *
 * Zustand store for tracking recently opened/saved .sql files.
 * Persists metadata across sessions via localStorage.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ScriptEntry,
  ScriptFileType,
  DefaultWorkspaceState,
  ScriptWorkspace,
  MountedFolder,
  MountedFolderBlueprintMetadata,
  SectionCollapseState,
} from '../types/scriptBrowserTypes';
import { getFileNameFromPath } from '../../workspace/query-editor/utils/queryFileUtils';
import {
  setActiveLink,
  addBlueprintToRegistry,
  removeBlueprintFromRegistry,
} from '../../database-blueprint/services/databaseBlueprintApi';
import { toast } from '../../../components/ui/toaster';
import { createGuardedJsonStorage } from '../../../core/storage/guardedJsonStorage';
import { showPersistWriteFailureToast } from '../../../components/ui/persistWriteFailureToast';

type ScriptBrowserState = {
  readonly scripts: ScriptEntry[];
  readonly workspaces: ScriptWorkspace[];
  readonly defaultWorkspaceState: DefaultWorkspaceState;
  readonly activeWorkspaceId: string | null;
  readonly scriptRetentionHintCount: number;
  readonly sectionCollapseState: SectionCollapseState;

  readonly recordScriptOpened: (filePath: string, preview?: string) => void;
  readonly recordScriptSaved: (
    filePath: string,
    contentHash: string,
    connectionInfo?: { connectionId?: string; connectionName?: string }
  ) => void;
  readonly removeScript: (id: string) => void;
  readonly restoreScript: (entry: ScriptEntry) => void;
  readonly togglePin: (id: string) => void;
  readonly setConnectionAssociation: (
    scriptId: string,
    connectionId?: string,
    connectionName?: string
  ) => void;
  readonly saveAsWorkspace: (name: string) => void;
  readonly switchWorkspace: (id: string | null) => void;
  readonly deleteWorkspace: (id: string) => void;
  readonly renameWorkspace: (id: string, name: string) => void;
  readonly addMountedFolder: (directoryPath: string) => void;
  readonly removeMountedFolder: (id: string) => void;
  readonly renameMountedFolder: (id: string, label: string) => void;
  /**
   * Promote / refresh a mounted folder to be a Database Blueprint. Creates the
   * folder if it isn't already mounted, then sets `kind: 'blueprint'` and
   * stores the lean blueprint metadata.
   */
  readonly markFolderAsBlueprint: (
    directoryPath: string,
    label: string,
    metadata: MountedFolderBlueprintMetadata
  ) => string;
  /** Patch the in-memory blueprint metadata (e.g. after Refresh / Apply). */
  readonly updateBlueprintMetadata: (
    id: string,
    patch: Partial<MountedFolderBlueprintMetadata>
  ) => void;
  /**
   * Update the persisted "active link" for a blueprint folder. Optimistically
   * patches in-memory metadata, calls the `database-blueprint-set-active-link`
   * IPC to write `jamsql-blueprint.json`, and rolls back + toasts on failure.
   * Returns `true` on success (including no-op when the label is already
   * active) and `false` on failure or when the folder isn't a blueprint.
   */
  readonly updateBlueprintActiveLink: (
    folderId: string,
    label: string
  ) => Promise<boolean>;
  readonly setSectionCollapse: (section: keyof SectionCollapseState, collapsed: boolean) => void;
};

/** Normalize file path for de-duplication (forward slashes, trim). */
const normalizePath = (filePath: string): string =>
  filePath.replace(/\\/g, '/').trim();

/** Detect file type from extension. */
const detectFileType = (filePath: string): ScriptFileType =>
  filePath.toLowerCase().endsWith('.ipynb') ? 'ipynb' : 'sql';

export const useScriptBrowserStore = create<ScriptBrowserState>()(
  persist(
    (set, get) => ({
      scripts: [],
      workspaces: [],
      defaultWorkspaceState: { pinnedScriptIds: [], mountedFolders: [] },
      activeWorkspaceId: null,
      scriptRetentionHintCount: 0,
      sectionCollapseState: {},

      recordScriptOpened: (filePath: string, preview?: string) => {
        const normalized = normalizePath(filePath);
        const now = new Date().toISOString();

        set((state) => {
          const existing = state.scripts.find(
            (s) => normalizePath(s.filePath) === normalized
          );

          if (existing) {
            return {
              scripts: state.scripts.map((s) =>
                s.id === existing.id
                  ? {
                      ...s,
                      lastOpenedAt: now,
                      ...(preview !== undefined ? { preview } : {}),
                    }
                  : s
              ),
            };
          }

          const entry: ScriptEntry = {
            id: crypto.randomUUID(),
            filePath: normalized,
            fileName: getFileNameFromPath(normalized),
            fileType: detectFileType(normalized),
            lastOpenedAt: now,
            ...(preview !== undefined ? { preview } : {}),
          };

          return { scripts: [entry, ...state.scripts] };
        });
      },

      recordScriptSaved: (
        filePath: string,
        contentHash: string,
        connectionInfo?: { connectionId?: string; connectionName?: string }
      ) => {
        const normalized = normalizePath(filePath);
        const now = new Date().toISOString();

        set((state) => {
          const existing = state.scripts.find(
            (s) => normalizePath(s.filePath) === normalized
          );

          if (existing) {
            // Auto-associate connection if not already set
            const connUpdate =
              connectionInfo?.connectionId && !existing.connectionId
                ? {
                    connectionId: connectionInfo.connectionId,
                    connectionName: connectionInfo.connectionName,
                  }
                : {};

            return {
              scripts: state.scripts.map((s) =>
                s.id === existing.id
                  ? { ...s, lastSavedAt: now, contentHash, ...connUpdate }
                  : s
              ),
            };
          }

          const entry: ScriptEntry = {
            id: crypto.randomUUID(),
            filePath: normalized,
            fileName: getFileNameFromPath(normalized),
            fileType: detectFileType(normalized),
            lastOpenedAt: now,
            lastSavedAt: now,
            contentHash,
            ...(connectionInfo?.connectionId
              ? {
                  connectionId: connectionInfo.connectionId,
                  connectionName: connectionInfo.connectionName,
                }
              : {}),
          };

          return { scripts: [entry, ...state.scripts] };
        });
      },

      removeScript: (id: string) => {
        set((state) => ({
          scripts: state.scripts.filter((s) => s.id !== id),
        }));
      },

      restoreScript: (entry: ScriptEntry) => {
        set((state) => {
          const alreadyExists = state.scripts.some((s) => s.id === entry.id);
          if (alreadyExists) return state;
          return { scripts: [entry, ...state.scripts] };
        });
      },

      togglePin: (id: string) => {
        set((state) => {
          const ws = state.activeWorkspaceId
            ? state.workspaces.find((w) => w.id === state.activeWorkspaceId)
            : null;

          if (ws) {
            // Named workspace — toggle in workspace's pinnedScriptIds
            const has = ws.pinnedScriptIds.includes(id);
            return {
              workspaces: state.workspaces.map((w) =>
                w.id === ws.id
                  ? {
                      ...w,
                      pinnedScriptIds: has
                        ? w.pinnedScriptIds.filter((pid) => pid !== id)
                        : [...w.pinnedScriptIds, id],
                    }
                  : w
              ),
            };
          }

          // Default workspace
          const has = state.defaultWorkspaceState.pinnedScriptIds.includes(id);
          return {
            defaultWorkspaceState: {
              ...state.defaultWorkspaceState,
              pinnedScriptIds: has
                ? state.defaultWorkspaceState.pinnedScriptIds.filter(
                    (pid) => pid !== id
                  )
                : [...state.defaultWorkspaceState.pinnedScriptIds, id],
            },
          };
        });
      },

      setConnectionAssociation: (
        scriptId: string,
        connectionId?: string,
        connectionName?: string
      ) => {
        set((state) => ({
          scripts: state.scripts.map((s) =>
            s.id === scriptId ? { ...s, connectionId, connectionName } : s
          ),
        }));
      },

      saveAsWorkspace: (name: string) => {
        set((state) => {
          const ws: ScriptWorkspace = {
            id: crypto.randomUUID(),
            name,
            createdAt: new Date().toISOString(),
            pinnedScriptIds: [...state.defaultWorkspaceState.pinnedScriptIds],
            mountedFolders: [...state.defaultWorkspaceState.mountedFolders],
          };
          return {
            workspaces: [...state.workspaces, ws],
            activeWorkspaceId: ws.id,
          };
        });
      },

      switchWorkspace: (id: string | null) => {
        set({ activeWorkspaceId: id });
      },

      deleteWorkspace: (id: string) => {
        set((state) => {
          const ws = state.workspaces.find((w) => w.id === id);
          if (!ws) return state;

          // Merge pins into default (avoid duplicates)
          const existingPins = new Set(state.defaultWorkspaceState.pinnedScriptIds);
          const mergedPins = [
            ...state.defaultWorkspaceState.pinnedScriptIds,
            ...ws.pinnedScriptIds.filter((pid) => !existingPins.has(pid)),
          ];

          return {
            workspaces: state.workspaces.filter((w) => w.id !== id),
            activeWorkspaceId:
              state.activeWorkspaceId === id ? null : state.activeWorkspaceId,
            defaultWorkspaceState: {
              ...state.defaultWorkspaceState,
              pinnedScriptIds: mergedPins,
            },
          };
        });
      },

      renameWorkspace: (id: string, name: string) => {
        set((state) => ({
          workspaces: state.workspaces.map((w) =>
            w.id === id ? { ...w, name } : w
          ),
        }));
      },

      addMountedFolder: (directoryPath: string) => {
        const folder: MountedFolder = {
          id: crypto.randomUUID(),
          directoryPath: directoryPath.replace(/\\/g, '/').trim(),
          label: directoryPath.split(/[/\\]/).filter(Boolean).pop() ?? directoryPath,
          mountedAt: new Date().toISOString(),
          kind: 'normal',
        };

        set((state) => {
          const ws = state.activeWorkspaceId
            ? state.workspaces.find((w) => w.id === state.activeWorkspaceId)
            : null;

          if (ws) {
            return {
              workspaces: state.workspaces.map((w) =>
                w.id === ws.id
                  ? { ...w, mountedFolders: [...w.mountedFolders, folder] }
                  : w
              ),
            };
          }

          return {
            defaultWorkspaceState: {
              ...state.defaultWorkspaceState,
              mountedFolders: [
                ...state.defaultWorkspaceState.mountedFolders,
                folder,
              ],
            },
          };
        });
      },

      removeMountedFolder: (id: string) => {
        // Capture the folder BEFORE the set() so we can write-through a
        // registry-remove only when the removed entry was a blueprint.
        const state = get();
        const ws = state.activeWorkspaceId
          ? state.workspaces.find((w) => w.id === state.activeWorkspaceId)
          : null;
        const folders = ws ? ws.mountedFolders : state.defaultWorkspaceState.mountedFolders;
        const removed = folders.find((f) => f.id === id);

        set((s) => {
          const activeWs = s.activeWorkspaceId
            ? s.workspaces.find((w) => w.id === s.activeWorkspaceId)
            : null;
          if (activeWs) {
            return {
              workspaces: s.workspaces.map((w) =>
                w.id === activeWs.id
                  ? { ...w, mountedFolders: w.mountedFolders.filter((f) => f.id !== id) }
                  : w,
              ),
            };
          }
          return {
            defaultWorkspaceState: {
              ...s.defaultWorkspaceState,
              mountedFolders: s.defaultWorkspaceState.mountedFolders.filter((f) => f.id !== id),
            },
          };
        });

        if (removed && removed.kind === 'blueprint') {
          void removeBlueprintFromRegistry(removed.directoryPath);
        }
      },

      renameMountedFolder: (id: string, label: string) => {
        const trimmed = label.trim();
        if (!trimmed) return;

        set((state) => {
          const ws = state.activeWorkspaceId
            ? state.workspaces.find((w) => w.id === state.activeWorkspaceId)
            : null;

          if (ws) {
            return {
              workspaces: state.workspaces.map((w) =>
                w.id === ws.id
                  ? {
                      ...w,
                      mountedFolders: w.mountedFolders.map((f) =>
                        f.id === id ? { ...f, label: trimmed } : f
                      ),
                    }
                  : w
              ),
            };
          }

          return {
            defaultWorkspaceState: {
              ...state.defaultWorkspaceState,
              mountedFolders: state.defaultWorkspaceState.mountedFolders.map((f) =>
                f.id === id ? { ...f, label: trimmed } : f
              ),
            },
          };
        });
      },

      markFolderAsBlueprint: (
        directoryPath: string,
        label: string,
        metadata: MountedFolderBlueprintMetadata
      ): string => {
        const normalizedPath = directoryPath.replace(/\\/g, '/').trim();
        const trimmedLabel = label.trim() || normalizedPath.split('/').filter(Boolean).pop() || normalizedPath;
        let resultId = '';

        set((state) => {
          const ws = state.activeWorkspaceId
            ? state.workspaces.find((w) => w.id === state.activeWorkspaceId)
            : null;

          const upsert = (folders: MountedFolder[]): MountedFolder[] => {
            const existing = folders.find((f) => f.directoryPath === normalizedPath);
            if (existing) {
              resultId = existing.id;
              return folders.map((f) =>
                f.id === existing.id
                  ? {
                      ...f,
                      label: trimmedLabel,
                      kind: 'blueprint' as const,
                      blueprintMetadata: metadata,
                    }
                  : f
              );
            }
            const newFolder: MountedFolder = {
              id: crypto.randomUUID(),
              directoryPath: normalizedPath,
              label: trimmedLabel,
              mountedAt: new Date().toISOString(),
              kind: 'blueprint',
              blueprintMetadata: metadata,
            };
            resultId = newFolder.id;
            return [...folders, newFolder];
          };

          if (ws) {
            return {
              workspaces: state.workspaces.map((w) =>
                w.id === ws.id ? { ...w, mountedFolders: upsert(w.mountedFolders) } : w
              ),
            };
          }

          return {
            defaultWorkspaceState: {
              ...state.defaultWorkspaceState,
              mountedFolders: upsert(state.defaultWorkspaceState.mountedFolders),
            },
          };
        });

        // Write-through to the main-process registry (projection contract).
        // Fire-and-forget — the in-memory store is authoritative for the UI;
        // the registry is the cross-process source of truth for which
        // blueprints exist (headless/MCP/next-launch hydrate).
        void addBlueprintToRegistry({ folderPath: normalizedPath, label: trimmedLabel });

        return resultId;
      },

      updateBlueprintMetadata: (id: string, patch: Partial<MountedFolderBlueprintMetadata>) => {
        set((state) => {
          const apply = (folders: MountedFolder[]): MountedFolder[] =>
            folders.map((f) =>
              f.id === id && f.kind === 'blueprint' && f.blueprintMetadata
                ? { ...f, blueprintMetadata: { ...f.blueprintMetadata, ...patch } }
                : f
            );

          const ws = state.activeWorkspaceId
            ? state.workspaces.find((w) => w.id === state.activeWorkspaceId)
            : null;

          if (ws) {
            return {
              workspaces: state.workspaces.map((w) =>
                w.id === ws.id ? { ...w, mountedFolders: apply(w.mountedFolders) } : w
              ),
            };
          }
          return {
            defaultWorkspaceState: {
              ...state.defaultWorkspaceState,
              mountedFolders: apply(state.defaultWorkspaceState.mountedFolders),
            },
          };
        });
      },

      updateBlueprintActiveLink: async (folderId: string, label: string): Promise<boolean> => {
        // Look up the folder before touching anything else. We need the
        // directoryPath for the IPC and the previous activeLinkLabel for
        // the rollback path.
        const findFolder = (): MountedFolder | undefined => {
          const state = get();
          const ws = state.activeWorkspaceId
            ? state.workspaces.find((w) => w.id === state.activeWorkspaceId)
            : null;
          const folders = ws
            ? ws.mountedFolders
            : state.defaultWorkspaceState.mountedFolders;
          return folders.find((f) => f.id === folderId);
        };

        const before = findFolder();
        if (!before || before.kind !== 'blueprint' || !before.blueprintMetadata) {
          console.error(
            '[scriptBrowserStore] updateBlueprintActiveLink called for non-blueprint folder',
            folderId,
          );
          return false;
        }
        const previousActiveLabel = before.blueprintMetadata.activeLinkLabel;
        if (previousActiveLabel === label) {
          return true; // no-op
        }
        const folderPath = before.directoryPath;

        // Optimistically apply the change in memory so the UI updates
        // immediately. We roll this back if the IPC fails.
        const applyActiveLabel = (
          folders: MountedFolder[],
          nextLabel: string | undefined,
        ): MountedFolder[] =>
          folders.map((f) => {
            if (f.id !== folderId || f.kind !== 'blueprint' || !f.blueprintMetadata) {
              return f;
            }
            const nextMeta: MountedFolderBlueprintMetadata = { ...f.blueprintMetadata };
            if (nextLabel === undefined) {
              delete nextMeta.activeLinkLabel;
            } else {
              nextMeta.activeLinkLabel = nextLabel;
            }
            return { ...f, blueprintMetadata: nextMeta };
          });

        const patchActiveLabel = (nextLabel: string | undefined) => {
          set((state) => {
            const ws = state.activeWorkspaceId
              ? state.workspaces.find((w) => w.id === state.activeWorkspaceId)
              : null;
            if (ws) {
              return {
                workspaces: state.workspaces.map((w) =>
                  w.id === ws.id
                    ? { ...w, mountedFolders: applyActiveLabel(w.mountedFolders, nextLabel) }
                    : w,
                ),
              };
            }
            return {
              defaultWorkspaceState: {
                ...state.defaultWorkspaceState,
                mountedFolders: applyActiveLabel(
                  state.defaultWorkspaceState.mountedFolders,
                  nextLabel,
                ),
              },
            };
          });
        };

        patchActiveLabel(label);

        const result = await setActiveLink({ folderPath, label });
        if (!result.success) {
          // Roll back the optimistic update and toast the error so the user
          // sees why the chip snapped back.
          patchActiveLabel(previousActiveLabel);
          toast.error({
            title: 'Could not switch active link',
            description: result.error,
          });
          return false;
        }

        // Sync any canonical changes from the on-disk record (currently just
        // activeLinkLabel, but writeBlueprintMetadata may normalise other fields
        // in the future).
        patchActiveLabel(result.metadata.activeLinkLabel ?? label);
        return true;
      },

      setSectionCollapse: (section, collapsed) => {
        set((state) => ({
          sectionCollapseState: {
            ...state.sectionCollapseState,
            [section]: collapsed,
          },
        }));
      },
    }),
    {
      name: 'sql-studio-script-browser',
      storage: createGuardedJsonStorage({ onFailure: showPersistWriteFailureToast('the Scripts panel') }),
      version: 3,
      partialize: (state) => ({
        scripts: state.scripts,
        workspaces: state.workspaces,
        defaultWorkspaceState: state.defaultWorkspaceState,
        activeWorkspaceId: state.activeWorkspaceId,
        scriptRetentionHintCount: state.scriptRetentionHintCount,
        sectionCollapseState: state.sectionCollapseState,
      }),
      migrate: (persistedState, version) => {
        if (!persistedState || typeof persistedState !== 'object') {
          return {
            scripts: [],
            workspaces: [],
            defaultWorkspaceState: { pinnedScriptIds: [], mountedFolders: [] },
            activeWorkspaceId: null,
            scriptRetentionHintCount: 0,
            sectionCollapseState: {},
          };
        }

        // v1 → v2: backfill fileType on existing entries
        if (version === 1) {
          const state = persistedState as Record<string, unknown>;
          const scripts = Array.isArray(state.scripts) ? state.scripts : [];
          state.scripts = scripts.map((s: Record<string, unknown>) => ({
            ...s,
            fileType: s.fileType ?? detectFileType(String(s.filePath ?? '')),
          }));
        }

        // v2 → v3: default `kind` to 'normal' on every mounted folder so the
        // new Database Blueprint branch only activates for entries that explicitly
        // declare `kind: 'blueprint'`.
        if (version === 1 || version === 2) {
          const state = persistedState as Record<string, unknown>;
          const backfillKind = (f: unknown): Record<string, unknown> => {
            const rec = (f ?? {}) as Record<string, unknown>;
            return { ...rec, kind: rec.kind ?? 'normal' };
          };
          const defaultWs = state.defaultWorkspaceState as
            | { mountedFolders?: unknown[] }
            | undefined;
          if (defaultWs && Array.isArray(defaultWs.mountedFolders)) {
            defaultWs.mountedFolders = defaultWs.mountedFolders.map(backfillKind);
          }
          const workspaces = state.workspaces as
            | Array<{ mountedFolders?: unknown[] }>
            | undefined;
          if (Array.isArray(workspaces)) {
            for (const ws of workspaces) {
              if (Array.isArray(ws.mountedFolders)) {
                ws.mountedFolders = ws.mountedFolders.map(backfillKind);
              }
            }
          }
        }

        return persistedState;
      },
    }
  )
);
